interface AuthFailureLike {
  statusCode?: number;
  status?: number;
  code?: unknown;
  message?: unknown;
}

function authFailureText(value: unknown) {
  return typeof value === 'string' ? value.toLowerCase() : '';
}

function isExpiredJwtAuthFailure(error: unknown) {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const failure = error as AuthFailureLike;
  const statusCode = failure.statusCode ?? failure.status;
  if (statusCode !== 401) {
    return false;
  }

  const code = authFailureText(failure.code);
  const message = authFailureText(failure.message);

  return (
    code === 'token_expired' ||
    code === 'jwt_expired' ||
    code === 'expired_token' ||
    (message.includes('jwt') && message.includes('expired')) ||
    (message.includes('access token') && message.includes('expired'))
  );
}

export async function retryOnExpiredJwt<TToken, TResult>({
  token,
  request,
  refreshToken,
}: {
  token: TToken;
  request: (token: TToken) => Promise<TResult>;
  refreshToken: (expiredToken: TToken) => Promise<TToken | null | undefined>;
}) {
  try {
    return await request(token);
  } catch (error) {
    if (!isExpiredJwtAuthFailure(error)) {
      throw error;
    }

    const refreshedToken = await refreshToken(token);
    if (!refreshedToken) {
      throw error;
    }

    return request(refreshedToken);
  }
}

export function createTokenRefreshCoordinator<TInput, TResult>(
  refreshToken: (expiredToken?: TInput) => Promise<TResult>,
) {
  let refreshPromise: Promise<TResult> | null = null;

  return async (expiredToken?: TInput) => {
    if (!refreshPromise) {
      refreshPromise = refreshToken(expiredToken).finally(() => {
        refreshPromise = null;
      });
    }

    return refreshPromise;
  };
}
